import React, { useState, useEffect } from "react";
import { Select, Input, Modal, message } from "antd";
import useAuth from "@/hooks/useAuth";
import {
  PlusOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  useGetEventTypeQuery,
  useAddEventTypeMutation,
  useUpdateEventTypeMutation,
  useDeleteEventTypeMutation,
} from "@/services/api/event/eventTypeApi";
import {
  useGetDietaryPreferencesQuery,
  useAddDietaryPreferenceMutation,
  useUpdateDietaryPreferenceMutation,
  useDeleteDietaryPreferencesMutation,
} from "@/services/api/event/dietarypreferencesApi";
import {
  useGetBeverageTypeQuery,
  useAddBeverageTypeMutation,
  useUpdateBeverageTypeMutation,
  useDeleteBeverageTypeMutation,
} from "@/services/api/event/beverageTypeApi";
import {
  useGetPackageTypeQuery,
  useAddPackageTypeMutation,
  useUpdatePackageTypeMutation,
  useDeletePackageTypeMutation,
} from "@/services/api/event/packageTypeApi";
import {
  useGetServiceTypeQuery,
  useAddServiceTypeMutation,
  useUpdateServiceTypeMutation,
  useDeleteServiceTypeMutation,
} from "@/services/api/event/serviceTypeApi";
import {
  useGetUnitOfMeasurementQuery,
  useAddUnitOfMeasurementMutation,
  useDeleteUnitOfMeasurementMutation,
  useUpdateUnitOfMeasurementMutation,
} from "@/services/api/unitOfMeasurement/unitOfMeasurementApi";
import {
  useAddDeliveryModeMutation,
  useDeleteDeliveryModeMutation,
  useGetDeliveryModesQuery,
  useUpdateDeliveryModeMutation,
} from "@/services/api/deliveryMode/deliveryModeApi";

const GeneralSetup = () => {
  const authUser = useAuth();

  // Event Type
  const {
    data: eventTypesData,
    isLoading: isLoadingEventTypes,
    error: eventTypesError,
  } = useGetEventTypeQuery();
  const [addEventType, { isLoading: isAddingEventType }] =
    useAddEventTypeMutation();
  const [updateEventType, { isLoading: isUpdatingEventType }] =
    useUpdateEventTypeMutation();
  const [deleteEventType, { isLoading: isDeletingEventType }] =
    useDeleteEventTypeMutation();

  // Dietary Preferences
  const {
    data: dietaryPreferencesData,
    isLoading: isLoadingDietaryPreferences,
    error: dietaryPreferencesError,
  } = useGetDietaryPreferencesQuery();
  const [addDietaryPreference, { isLoading: isAddingDietaryPreference }] =
    useAddDietaryPreferenceMutation();
  const [updateDietaryPreference, { isLoading: isUpdatingDietaryPreference }] =
    useUpdateDietaryPreferenceMutation();
  const [deleteDietaryPreference, { isLoading: isDeletingDietaryPreference }] =
    useDeleteDietaryPreferencesMutation();

  // Beverage Type
  const {
    data: beverageTypesData,
    isLoading: isLoadingBeverageTypes,
    error: beverageTypesError,
  } = useGetBeverageTypeQuery();
  const [addBeverageType, { isLoading: isAddingBeverageType }] =
    useAddBeverageTypeMutation();
  const [updateBeverageType, { isLoading: isUpdatingBeverageType }] =
    useUpdateBeverageTypeMutation();
  const [deleteBeverageType, { isLoading: isDeletingBeverageType }] =
    useDeleteBeverageTypeMutation();

  // Package Type
  const {
    data: packageTypesData,
    isLoading: isLoadingPackageTypes,
    error: packageTypesError,
  } = useGetPackageTypeQuery();
  const [addPackageType, { isLoading: isAddingPackageType }] =
    useAddPackageTypeMutation();
  const [updatePackageType, { isLoading: isUpdatingPackageType }] =
    useUpdatePackageTypeMutation();
  const [deletePackageType, { isLoading: isDeletingPackageType }] =
    useDeletePackageTypeMutation();

  // Service Type
  const {
    data: serviceTypesData,
    isLoading: isLoadingServiceTypes,
    error: serviceTypesError,
  } = useGetServiceTypeQuery();
  const [addServiceType, { isLoading: isAddingServiceType }] =
    useAddServiceTypeMutation();
  const [updateServiceType, { isLoading: isUpdatingServiceType }] =
    useUpdateServiceTypeMutation();
  const [deleteServiceType, { isLoading: isDeletingServiceType }] =
    useDeleteServiceTypeMutation();

  // Unit of Measurement
  const {
    data: unitOfMeasurementData,
    isLoading: isLoadingUnitOfMeasurement,
    error: unitOfMeasurementError,
  } = useGetUnitOfMeasurementQuery();
  const [addUnitOfMeasurement, { isLoading: isAddingMeasurement }] =
    useAddUnitOfMeasurementMutation();
  const [updateUnitOfMeasurement, { isLoading: isUpdatingMeasurement }] =
    useUpdateUnitOfMeasurementMutation();
  const [deleteUnitOfMeasurement, { isLoading: isDeletingMeasurement }] =
    useDeleteUnitOfMeasurementMutation();

  // Delivery Mode
  const {
    data: deliveryModesData,
    isLoading: isLoadingDeliveryModes,
    error: deliveryModesError,
  } = useGetDeliveryModesQuery();
  const [addDeliveryMode, { isLoading: isAddingDeliveryMode }] =
    useAddDeliveryModeMutation();
  const [updateDeliveryMode, { isLoading: isUpdatingDeliveryMode }] =
    useUpdateDeliveryModeMutation();
  const [deleteDeliveryMode, { isLoading: isDeletingDeliveryMode }] =
    useDeleteDeliveryModeMutation();

  const [settings, setSettings] = useState({
    eventType: [],
    dietaryPreferences: [],
    beverageType: [],
    packageType: [],
    serviceType: [],
    guestRangeOptions: ["100-150", "200-250", "More than 400"],
    unitOfMeasurement: [],
    deliveryMode: [],
  });

  const [inputValues, setInputValues] = useState({
    eventType: "",
    dietaryPreferences: "",
    beverageType: "",
    packageType: "",
    packageTypeAmount: "",
    serviceType: "",
    guestRangeOptions: "",
    unitOfMeasurement: "",
    unitOfMeasurementLabel: "",
    deliveryMode: "",
  });

  const [inputDescriptions, setInputDescriptions] = useState({
    eventType: "",
    dietaryPreferences: "",
    beverageType: "",
    packageType: "",
    serviceType: "",
    guestRangeOptions: "",
    unitOfMeasurement: "",
    deliveryMode: "",
  });

  const [showAddModal, setShowAddModal] = useState({
    eventType: false,
    dietaryPreferences: false,
    beverageType: false,
    packageType: false,
    serviceType: false,
    guestRangeOptions: false,
    unitOfMeasurement: false,
    deliveryMode: false,
  });

  const [showEditModal, setShowEditModal] = useState({
    eventType: false,
    dietaryPreferences: false,
    beverageType: false,
    packageType: false,
    serviceType: false,
    unitOfMeasurement: false,
    deliveryMode: false,
  });

  const [editValues, setEditValues] = useState({
    eventType: { id: null, name: "", originalName: "", description: "" },
    dietaryPreferences: {
      id: null,
      name: "",
      originalName: "",
      description: "",
    },
    beverageType: { id: null, name: "", originalName: "", description: "" },
    packageType: {
      id: null,
      name: "",
      amount: "",
      originalName: "",
      description: "",
    },
    serviceType: { id: null, name: "", originalName: "", description: "" },
    unitOfMeasurement: {
      id: null,
      name: "",
      label: "",
      originalName: "",
      description: "",
    },
    deliveryMode: { id: null, name: "", originalName: "", description: "" },
  });

  useEffect(() => {
    if (eventTypesData && Array.isArray(eventTypesData)) {
      setSettings((prev) => ({
        ...prev,
        eventType: eventTypesData.map(
          (item) => item.name || item.title || item,
        ),
      }));
    }
  }, [eventTypesData]);

  useEffect(() => {
    if (dietaryPreferencesData && Array.isArray(dietaryPreferencesData)) {
      setSettings((prev) => ({
        ...prev,
        dietaryPreferences: dietaryPreferencesData.map(
          (item) => item.name || item.title || item,
        ),
      }));
    }
  }, [dietaryPreferencesData]);

  useEffect(() => {
    if (beverageTypesData && Array.isArray(beverageTypesData)) {
      setSettings((prev) => ({
        ...prev,
        beverageType: beverageTypesData.map(
          (item) => item.name || item.title || item,
        ),
      }));
    }
  }, [beverageTypesData]);

  useEffect(() => {
    if (packageTypesData && Array.isArray(packageTypesData)) {
      setSettings((prev) => ({
        ...prev,
        packageType: packageTypesData.map(
          (item) => item.name || item.title || item,
        ),
      }));
    }
  }, [packageTypesData]);

  useEffect(() => {
    if (serviceTypesData && Array.isArray(serviceTypesData)) {
      setSettings((prev) => ({
        ...prev,
        serviceType: serviceTypesData.map(
          (item) => item.name || item.title || item,
        ),
      }));
    }
  }, [serviceTypesData]);

  useEffect(() => {
    if (unitOfMeasurementData && Array.isArray(unitOfMeasurementData)) {
      setSettings((prev) => ({
        ...prev,
        unitOfMeasurement: unitOfMeasurementData.map(
          (item) => item.name || item.title || item,
        ),
      }));
    }
  }, [unitOfMeasurementData]);

  useEffect(() => {
    if (deliveryModesData && Array.isArray(deliveryModesData)) {
      setSettings((prev) => ({
        ...prev,
        deliveryMode: deliveryModesData.map(
          (item) => item.name || item.title || item,
        ),
      }));
    }
  }, [deliveryModesData]);

  const handleAddTag = async (category, value) => {
    if (value.trim() && !settings[category].includes(value.trim())) {
      if (category === "eventType") {
        try {
          await addEventType({ name: value.trim() }).unwrap();
          message.success("Event type added successfully!");
          setInputValues((prev) => ({ ...prev, [category]: "" }));
          setInputDescriptions((prev) => ({ ...prev, [category]: "" }));
          setShowAddModal((prev) => ({ ...prev, [category]: false }));
        } catch (error) {
          message.error("Failed to add event type. Please try again.");
          console.error("Error adding event type:", error);
        }
      } else if (category === "dietaryPreferences") {
        try {
          await addDietaryPreference({
            name: value.trim(),
            description: inputDescriptions[category].trim() || "",
            created_by: authUser?.id,
          }).unwrap();
          message.success("Dietary preference added successfully!");
          setInputValues((prev) => ({ ...prev, [category]: "" }));
          setInputDescriptions((prev) => ({ ...prev, [category]: "" }));
          setShowAddModal((prev) => ({ ...prev, [category]: false }));
        } catch (error) {
          message.error("Failed to add dietary preference. Please try again.");
          console.error("Error adding dietary preference:", error);
        }
      } else if (category === "beverageType") {
        try {
          await addBeverageType({
            name: value.trim(),
            description: inputDescriptions[category].trim() || "",
            created_by: authUser?.id,
          }).unwrap();
          message.success("Beverage type added successfully!");
          setInputValues((prev) => ({ ...prev, [category]: "" }));
          setInputDescriptions((prev) => ({ ...prev, [category]: "" }));
          setShowAddModal((prev) => ({ ...prev, [category]: false }));
        } catch (error) {
          message.error("Failed to add beverage type. Please try again.");
          console.error("Error adding beverage type:", error);
        }
      } else if (category === "packageType") {
        try {
          const payload = {
            name: value.trim(),
            description: inputDescriptions[category].trim() || "",
            created_by: authUser?.id,
          };
          const rawAmount = inputValues.packageTypeAmount;
          if (rawAmount !== "" && rawAmount !== undefined) {
            const parsedAmount = parseFloat(
              String(rawAmount).replace(/,/g, ""),
            );
            if (!isNaN(parsedAmount) && parsedAmount >= 0) {
              payload.amount = parsedAmount;
            }
          }
          await addPackageType(payload).unwrap();
          message.success("Package type added successfully!");
          setInputValues((prev) => ({
            ...prev,
            [category]: "",
            packageTypeAmount: "",
          }));
          setInputDescriptions((prev) => ({ ...prev, [category]: "" }));
          setShowAddModal((prev) => ({ ...prev, [category]: false }));
        } catch (error) {
          message.error("Failed to add package type. Please try again.");
          console.error("Error adding package type:", error);
        }
      } else if (category === "serviceType") {
        try {
          await addServiceType({
            name: value.trim(),
            description: inputDescriptions[category].trim() || "",
            created_by: authUser?.id,
          }).unwrap();
          message.success("Service type added successfully!");
          setInputValues((prev) => ({ ...prev, [category]: "" }));
          setInputDescriptions((prev) => ({ ...prev, [category]: "" }));
          setShowAddModal((prev) => ({ ...prev, [category]: false }));
        } catch (error) {
          message.error("Failed to add service type. Please try again.");
          console.error("Error adding service type:", error);
        }
      } else if (category === "unitOfMeasurement") {
        try {
          await addUnitOfMeasurement({
            name: value.trim(),
            label: inputValues.unitOfMeasurementLabel.trim() || "",
            description: inputDescriptions[category].trim() || "",
            created_by: authUser?.id,
          }).unwrap();
          message.success("Unit of measurement added successfully!");
          setInputValues((prev) => ({
            ...prev,
            [category]: "",
            unitOfMeasurementLabel: "",
          }));
          setInputDescriptions((prev) => ({ ...prev, [category]: "" }));
          setShowAddModal((prev) => ({ ...prev, [category]: false }));
        } catch (error) {
          message.error("Failed to add unit of measurement. Please try again.");
          console.error("Error adding unit of measurement:", error);
        }
      } else if (category === "deliveryMode") {
        try {
          await addDeliveryMode({
            name: value.trim(),
            description: inputDescriptions[category].trim() || "",
            created_by: authUser?.id,
          }).unwrap();
          message.success("Delivery mode added successfully!");
          setInputValues((prev) => ({ ...prev, [category]: "" }));
          setInputDescriptions((prev) => ({ ...prev, [category]: "" }));
          setShowAddModal((prev) => ({ ...prev, [category]: false }));
        } catch (error) {
          message.error("Failed to add delivery mode. Please try again.");
          console.error("Error adding delivery mode:", error);
        }
      } else {
        setSettings((prev) => ({
          ...prev,
          [category]: [...prev[category], value.trim()],
        }));
        setInputValues((prev) => ({ ...prev, [category]: "" }));
        setShowAddModal((prev) => ({ ...prev, [category]: false }));
      }
    }
  };

  const handleRemoveTag = (category, tagToRemove) => {
    Modal.confirm({
      title: "Confirm Deletion",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to remove "${tagToRemove}"?`,
      okText: "Yes, Remove",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        if (category === "eventType") {
          try {
            const item = eventTypesData.find(
              (i) => (i.name || i.title || i) === tagToRemove,
            );
            if (item?.id) {
              await deleteEventType(item.id).unwrap();
              message.success("Event type deleted successfully!");
            } else {
              message.error(
                "Event type not found. Please refresh and try again.",
              );
            }
          } catch (error) {
            message.error("Failed to delete event type. Please try again.");
            console.error("Error deleting event type:", error);
          }
        } else if (category === "dietaryPreferences") {
          try {
            const item = dietaryPreferencesData.find(
              (i) => (i.name || i.title || i) === tagToRemove,
            );
            if (item?.id) {
              await deleteDietaryPreference(item.id).unwrap();
              message.success("Dietary preference deleted successfully!");
            } else {
              message.error(
                "Dietary preference not found. Please refresh and try again.",
              );
            }
          } catch (error) {
            message.error(
              "Failed to delete dietary preference. Please try again.",
            );
            console.error("Error deleting dietary preference:", error);
          }
        } else if (category === "beverageType") {
          try {
            const item = beverageTypesData.find(
              (i) => (i.name || i.title || i) === tagToRemove,
            );
            if (item?.id) {
              await deleteBeverageType(item.id).unwrap();
              message.success("Beverage type deleted successfully!");
            } else {
              message.error(
                "Beverage type not found. Please refresh and try again.",
              );
            }
          } catch (error) {
            message.error("Failed to delete beverage type. Please try again.");
            console.error("Error deleting beverage type:", error);
          }
        } else if (category === "packageType") {
          try {
            const item = packageTypesData.find(
              (i) => (i.name || i.title || i) === tagToRemove,
            );
            if (item?.id) {
              await deletePackageType(item.id).unwrap();
              message.success("Package type deleted successfully!");
            } else {
              message.error(
                "Package type not found. Please refresh and try again.",
              );
            }
          } catch (error) {
            message.error("Failed to delete package type. Please try again.");
            console.error("Error deleting package type:", error);
          }
        } else if (category === "serviceType") {
          try {
            const item = serviceTypesData.find(
              (i) => (i.name || i.title || i) === tagToRemove,
            );
            if (item?.id) {
              await deleteServiceType(item.id).unwrap();
              message.success("Service type deleted successfully!");
            } else {
              message.error(
                "Service type not found. Please refresh and try again.",
              );
            }
          } catch (error) {
            message.error("Failed to delete service type. Please try again.");
            console.error("Error deleting service type:", error);
          }
        } else if (category === "unitOfMeasurement") {
          try {
            const item = unitOfMeasurementData.find(
              (i) => (i.name || i.title || i) === tagToRemove,
            );
            if (item?.id) {
              await deleteUnitOfMeasurement(item.id).unwrap();
              message.success("Unit of measurement deleted successfully!");
            } else {
              message.error(
                "Unit of measurement not found. Please refresh and try again.",
              );
            }
          } catch (error) {
            message.error(
              "Failed to delete unit of measurement. Please try again.",
            );
            console.error("Error deleting unit of measurement:", error);
          }
        } else if (category === "deliveryMode") {
          try {
            const item = deliveryModesData.find(
              (i) => (i.name || i.title || i) === tagToRemove,
            );
            if (item?.id) {
              await deleteDeliveryMode(item.id).unwrap();
              message.success("Delivery mode deleted successfully!");
            } else {
              message.error(
                "Delivery mode not found. Please refresh and try again.",
              );
            }
          } catch (error) {
            message.error("Failed to delete delivery mode. Please try again.");
            console.error("Error deleting delivery mode:", error);
          }
        } else {
          setSettings((prev) => ({
            ...prev,
            [category]: prev[category].filter((tag) => tag !== tagToRemove),
          }));
        }
      },
    });
  };

  const handleInputChange = (category, value) => {
    setInputValues((prev) => ({ ...prev, [category]: value }));
  };

  const handleDescriptionChange = (category, value) => {
    setInputDescriptions((prev) => ({ ...prev, [category]: value }));
  };

  const handleKeyPress = (e, category) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag(category, inputValues[category]);
    }
  };

  const handleEditTag = (category, itemName) => {
    if (category === "eventType") {
      const item = eventTypesData.find(
        (i) => (i.name || i.title || i) === itemName,
      );
      setEditValues((prev) => ({
        ...prev,
        [category]: {
          id: item?.id || null,
          name: itemName,
          originalName: itemName,
        },
      }));
      setShowEditModal((prev) => ({ ...prev, [category]: true }));
    } else if (category === "dietaryPreferences") {
      const item = dietaryPreferencesData.find(
        (i) => (i.name || i.title || i) === itemName,
      );
      setEditValues((prev) => ({
        ...prev,
        [category]: {
          id: item?.id || null,
          name: itemName,
          originalName: itemName,
        },
      }));
      setShowEditModal((prev) => ({ ...prev, [category]: true }));
    } else if (category === "beverageType") {
      const item = beverageTypesData.find(
        (i) => (i.name || i.title || i) === itemName,
      );
      setEditValues((prev) => ({
        ...prev,
        [category]: {
          id: item?.id || null,
          name: itemName,
          originalName: itemName,
          description: item?.description || "",
        },
      }));
      setShowEditModal((prev) => ({ ...prev, [category]: true }));
    } else if (category === "packageType") {
      const item = packageTypesData.find(
        (i) => (i.name || i.title || i) === itemName,
      );
      setEditValues((prev) => ({
        ...prev,
        [category]: {
          id: item?.id || null,
          name: itemName,
          amount: item?.amount ?? "",
          originalName: itemName,
          description: item?.description || "",
        },
      }));
      setShowEditModal((prev) => ({ ...prev, [category]: true }));
    } else if (category === "serviceType") {
      const item = serviceTypesData.find(
        (i) => (i.name || i.title || i) === itemName,
      );
      setEditValues((prev) => ({
        ...prev,
        [category]: {
          id: item?.id || null,
          name: itemName,
          originalName: itemName,
          description: item?.description || "",
        },
      }));
      setShowEditModal((prev) => ({ ...prev, [category]: true }));
    } else if (category === "unitOfMeasurement") {
      const item = unitOfMeasurementData.find(
        (i) => (i.name || i.title || i) === itemName,
      );
      setEditValues((prev) => ({
        ...prev,
        [category]: {
          id: item?.id || null,
          name: itemName,
          label: item?.label || "",
          originalName: itemName,
          description: item?.description || "",
        },
      }));
      setShowEditModal((prev) => ({ ...prev, [category]: true }));
    } else if (category === "deliveryMode") {
      const item = deliveryModesData.find(
        (i) => (i.name || i.title || i) === itemName,
      );
      setEditValues((prev) => ({
        ...prev,
        [category]: {
          id: item?.id || null,
          name: itemName,
          originalName: itemName,
          description: item?.description || "",
        },
      }));
      setShowEditModal((prev) => ({ ...prev, [category]: true }));
    }
  };

  const handleUpdateTag = async (category, newValue) => {
    if (
      category === "eventType" &&
      newValue.trim() &&
      editValues[category].id
    ) {
      try {
        await updateEventType({
          id: editValues[category].id,
          requestData: { name: newValue.trim() },
        }).unwrap();
        message.success("Event type updated successfully!");
        setShowEditModal((prev) => ({ ...prev, [category]: false }));
        setEditValues((prev) => ({
          ...prev,
          [category]: { id: null, name: "", originalName: "" },
        }));
      } catch (error) {
        message.error("Failed to update event type. Please try again.");
        console.error("Error updating event type:", error);
      }
    } else if (
      category === "dietaryPreferences" &&
      newValue.trim() &&
      editValues[category].id
    ) {
      try {
        await updateDietaryPreference({
          id: editValues[category].id,
          requestData: { name: newValue.trim() },
        }).unwrap();
        message.success("Dietary preference updated successfully!");
        setShowEditModal((prev) => ({ ...prev, [category]: false }));
        setEditValues((prev) => ({
          ...prev,
          [category]: { id: null, name: "", originalName: "" },
        }));
      } catch (error) {
        message.error("Failed to update dietary preference. Please try again.");
        console.error("Error updating dietary preference:", error);
      }
    } else if (
      category === "beverageType" &&
      newValue.trim() &&
      editValues[category].id
    ) {
      try {
        await updateBeverageType({
          id: editValues[category].id,
          requestData: {
            name: newValue.trim(),
            description: editValues[category].description || "",
          },
        }).unwrap();
        message.success("Beverage type updated successfully!");
        setShowEditModal((prev) => ({ ...prev, [category]: false }));
        setEditValues((prev) => ({
          ...prev,
          [category]: { id: null, name: "", originalName: "", description: "" },
        }));
      } catch (error) {
        message.error("Failed to update beverage type. Please try again.");
        console.error("Error updating beverage type:", error);
      }
    } else if (
      category === "packageType" &&
      newValue.trim() &&
      editValues[category].id
    ) {
      try {
        const requestData = {
          name: newValue.trim(),
          description: editValues[category].description || "",
        };
        const rawAmount = editValues[category].amount;
        if (rawAmount !== "" && rawAmount !== undefined && rawAmount !== null) {
          const parsedAmount = parseFloat(String(rawAmount).replace(/,/g, ""));
          if (!isNaN(parsedAmount) && parsedAmount >= 0) {
            requestData.amount = parsedAmount;
          }
        }
        await updatePackageType({
          id: editValues[category].id,
          requestData,
        }).unwrap();
        message.success("Package type updated successfully!");
        setShowEditModal((prev) => ({ ...prev, [category]: false }));
        setEditValues((prev) => ({
          ...prev,
          [category]: {
            id: null,
            name: "",
            amount: "",
            originalName: "",
            description: "",
          },
        }));
      } catch (error) {
        message.error("Failed to update package type. Please try again.");
        console.error("Error updating package type:", error);
      }
    } else if (
      category === "serviceType" &&
      newValue.trim() &&
      editValues[category].id
    ) {
      try {
        await updateServiceType({
          id: editValues[category].id,
          requestData: {
            name: newValue.trim(),
            description: editValues[category].description || "",
          },
        }).unwrap();
        message.success("Service type updated successfully!");
        setShowEditModal((prev) => ({ ...prev, [category]: false }));
        setEditValues((prev) => ({
          ...prev,
          [category]: { id: null, name: "", originalName: "", description: "" },
        }));
      } catch (error) {
        message.error("Failed to update service type. Please try again.");
        console.error("Error updating service type:", error);
      }
    } else if (
      category === "unitOfMeasurement" &&
      newValue.trim() &&
      editValues[category].id
    ) {
      try {
        await updateUnitOfMeasurement({
          id: editValues[category].id,
          requestData: {
            name: newValue.trim(),
            label: editValues[category].label || "",
            description: editValues[category].description || "",
          },
        }).unwrap();
        message.success("Unit of measurement updated successfully!");
        setShowEditModal((prev) => ({ ...prev, [category]: false }));
        setEditValues((prev) => ({
          ...prev,
          [category]: {
            id: null,
            name: "",
            label: "",
            originalName: "",
            description: "",
          },
        }));
      } catch (error) {
        message.error(
          "Failed to update unit of measurement. Please try again.",
        );
        console.error("Error updating unit of measurement:", error);
      }
    } else if (
      category === "deliveryMode" &&
      newValue.trim() &&
      editValues[category].id
    ) {
      try {
        await updateDeliveryMode({
          id: editValues[category].id,
          requestData: {
            name: newValue.trim(),
            description: editValues[category].description || "",
          },
        }).unwrap();
        message.success("Delivery mode updated successfully!");
        setShowEditModal((prev) => ({ ...prev, [category]: false }));
        setEditValues((prev) => ({
          ...prev,
          [category]: { id: null, name: "", originalName: "", description: "" },
        }));
      } catch (error) {
        message.error("Failed to update delivery mode. Please try again.");
        console.error("Error updating delivery mode:", error);
      }
    }
  };

  const handleEditInputChange = (category, value) => {
    setEditValues((prev) => ({
      ...prev,
      [category]: { ...prev[category], name: value },
    }));
  };

  const handleEditLabelChange = (category, value) => {
    setEditValues((prev) => ({
      ...prev,
      [category]: { ...prev[category], label: value },
    }));
  };

  const handleEditDescriptionChange = (category, value) => {
    setEditValues((prev) => ({
      ...prev,
      [category]: { ...prev[category], description: value },
    }));
  };

  const handleEditAmountChange = (category, value) => {
    setEditValues((prev) => ({
      ...prev,
      [category]: { ...prev[category], amount: value },
    }));
  };

  const handleEditKeyPress = (e, category) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleUpdateTag(category, editValues[category].name);
    }
  };

  const toggleAddModal = (category) => {
    setShowAddModal((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleSelectChange = (category, values) => {
    setSettings((prev) => ({ ...prev, [category]: values }));
  };

  const setupSections = [
    {
      id: "eventType",
      title: "Event Type",
      description: "Set up different types of event",
      placeholder: "Add event type...",
    },
    {
      id: "dietaryPreferences",
      title: "Dietary Preferences",
      description: "e.g. Vegan, Gluten-free, Halal, etc.",
      placeholder: "Add dietary preference...",
    },
    {
      id: "beverageType",
      title: "Beverage Type",
      description: "e.g. Alcoholic, Non-alcoholic, etc.",
      placeholder: "Add beverage type...",
    },
    {
      id: "packageType",
      title: "Package Type",
      description: "e.g. Buffet, platter, food pack, etc.",
      placeholder: "Add package type...",
    },
    {
      id: "serviceType",
      title: "Service Type",
      description: "e.g. Drop-off, Delivery, Pickup",
      placeholder: "Add service type...",
    },
    {
      id: "guestRangeOptions",
      title: "Guest Range Options",
      description: "e.g. 100-150",
      placeholder: "Add guest range...",
    },
    {
      id: "unitOfMeasurement",
      title: "Unit of Measurement",
      description: "e.g. 1L, 5L, kg, pcs",
      placeholder: "Add unit of measurement...",
    },
    {
      id: "deliveryMode",
      title: "Delivery Mode",
      description: "e.g. Standard, Express, Same-day",
      placeholder: "Add delivery mode...",
    },
  ];

  const getIsLoading = (id) =>
    ({
      eventType: isLoadingEventTypes,
      dietaryPreferences: isLoadingDietaryPreferences,
      beverageType: isLoadingBeverageTypes,
      packageType: isLoadingPackageTypes,
      serviceType: isLoadingServiceTypes,
      unitOfMeasurement: isLoadingUnitOfMeasurement,
      deliveryMode: isLoadingDeliveryModes,
    })[id] ?? false;

  const getIsDeleting = (id) =>
    ({
      eventType: isDeletingEventType,
      dietaryPreferences: isDeletingDietaryPreference,
      beverageType: isDeletingBeverageType,
      packageType: isDeletingPackageType,
      serviceType: isDeletingServiceType,
      unitOfMeasurement: isDeletingMeasurement,
      deliveryMode: isDeletingDeliveryMode,
    })[id] ?? false;

  const getIsAdding = (id) =>
    ({
      eventType: isAddingEventType,
      dietaryPreferences: isAddingDietaryPreference,
      beverageType: isAddingBeverageType,
      packageType: isAddingPackageType,
      serviceType: isAddingServiceType,
      unitOfMeasurement: isAddingMeasurement,
      deliveryMode: isAddingDeliveryMode,
    })[id] ?? false;

  if (eventTypesError && !isLoadingEventTypes)
    message.error("Failed to load event types. Please refresh the page.");
  if (dietaryPreferencesError && !isLoadingDietaryPreferences)
    message.error(
      "Failed to load dietary preferences. Please refresh the page.",
    );
  if (beverageTypesError && !isLoadingBeverageTypes)
    message.error("Failed to load beverage types. Please refresh the page.");
  if (packageTypesError && !isLoadingPackageTypes)
    message.error("Failed to load package types. Please refresh the page.");
  if (serviceTypesError && !isLoadingServiceTypes)
    message.error("Failed to load service types. Please refresh the page.");
  if (unitOfMeasurementError && !isLoadingUnitOfMeasurement)
    message.error(
      "Failed to load units of measurement. Please refresh the page.",
    );
  if (deliveryModesError && !isLoadingDeliveryModes)
    message.error("Failed to load delivery modes. Please refresh the page.");

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
      <div className="space-y-6">
        {setupSections.map((section, index) => (
          <div key={section.id}>
            <div className="flex items-start gap-6">
              <div className="w-1/3">
                <h3 className="text-lg font-semibold text-gray-800 mb-1">
                  {section.title}
                </h3>
                <p className="text-sm text-gray-500">{section.description}</p>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Select
                    mode="multiple"
                    placeholder={`Select ${section.title.toLowerCase()}`}
                    value={settings[section.id]}
                    onChange={(values) =>
                      handleSelectChange(section.id, values)
                    }
                    style={{ width: "100%" }}
                    closable={false}
                    loading={getIsLoading(section.id)}
                    disabled={getIsLoading(section.id)}
                    dropdownRender={(menu) => (
                      <div>
                        <div className="max-h-60 overflow-y-auto">
                          {settings[section.id].map((item, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between w-full px-3 py-2 hover:bg-gray-50 cursor-pointer"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <span>{item}</span>
                              <div className="flex items-center gap-1">
                                {section.id !== "guestRangeOptions" && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleEditTag(section.id, item);
                                    }}
                                    className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded p-1"
                                    title="Edit"
                                  >
                                    <EditOutlined />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRemoveTag(section.id, item);
                                  }}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1"
                                  title="Delete"
                                  disabled={getIsDeleting(section.id)}
                                >
                                  <DeleteOutlined />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-gray-200 p-2">
                          <div
                            className="flex items-center gap-2 text-blue-600 cursor-pointer hover:bg-gray-50 p-2 rounded"
                            onClick={() => toggleAddModal(section.id)}
                          >
                            <PlusOutlined />
                            Add new {section.title.toLowerCase()}
                          </div>
                        </div>
                      </div>
                    )}
                    tagRender={(props) => {
                      const { label } = props;
                      return (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-50 border border-orange-200 text-orange-700 rounded-full text-sm mr-1">
                          {label}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemoveTag(section.id, label);
                            }}
                            className="ml-1 hover:bg-orange-100 rounded-full p-0.5 text-xs"
                            disabled={getIsDeleting(section.id)}
                          >
                            ×
                          </button>
                        </span>
                      );
                    }}
                  />
                </div>
              </div>
            </div>
            {index < setupSections.length - 1 && (
              <hr className="mt-6 border-gray-200" />
            )}
          </div>
        ))}
      </div>

      {/* Add Modals */}
      {setupSections.map((section) => (
        <Modal
          key={section.id}
          title={`Add New ${section.title}`}
          open={showAddModal[section.id]}
          onOk={() => handleAddTag(section.id, inputValues[section.id])}
          onCancel={() => {
            setShowAddModal((prev) => ({ ...prev, [section.id]: false }));
            setInputValues((prev) => ({
              ...prev,
              [section.id]: "",
              ...(section.id === "unitOfMeasurement" && {
                unitOfMeasurementLabel: "",
              }),
              ...(section.id === "packageType" && { packageTypeAmount: "" }),
            }));
            setInputDescriptions((prev) => ({ ...prev, [section.id]: "" }));
          }}
          okText="Add"
          cancelText="Cancel"
          confirmLoading={getIsAdding(section.id)}
        >
          <div className="py-4">
            <p className="text-gray-600 mb-4">{section.description}</p>
            <div className="space-y-4">
              <Input
                placeholder={section.placeholder}
                value={inputValues[section.id]}
                onChange={(e) => handleInputChange(section.id, e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, section.id)}
                autoFocus
              />

              {section.id === "packageType" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (Optional)
                  </label>
                  <Input
                    placeholder="e.g. 5000"
                    prefix="₦"
                    value={inputValues.packageTypeAmount}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, "");
                      if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
                        setInputValues((prev) => ({
                          ...prev,
                          packageTypeAmount: raw,
                        }));
                      }
                    }}
                  />
                </div>
              )}

              {section.id === "unitOfMeasurement" && (
                <Input
                  placeholder="Enter label (e.g., Kg, L, pcs)..."
                  value={inputValues.unitOfMeasurementLabel}
                  onChange={(e) =>
                    setInputValues((prev) => ({
                      ...prev,
                      unitOfMeasurementLabel: e.target.value,
                    }))
                  }
                />
              )}

              {[
                "dietaryPreferences",
                "beverageType",
                "packageType",
                "serviceType",
                "unitOfMeasurement",
                "deliveryMode",
              ].includes(section.id) && (
                <Input.TextArea
                  placeholder="Enter description (optional)..."
                  value={inputDescriptions[section.id]}
                  onChange={(e) =>
                    handleDescriptionChange(section.id, e.target.value)
                  }
                  rows={3}
                />
              )}
            </div>
          </div>
        </Modal>
      ))}

      {/* Edit Modal — Event Type */}
      <Modal
        title="Edit Event Type"
        open={showEditModal.eventType}
        onOk={() => handleUpdateTag("eventType", editValues.eventType.name)}
        onCancel={() => {
          setShowEditModal((prev) => ({ ...prev, eventType: false }));
          setEditValues((prev) => ({
            ...prev,
            eventType: { id: null, name: "", originalName: "" },
          }));
        }}
        okText="Update"
        cancelText="Cancel"
        confirmLoading={isUpdatingEventType}
      >
        <div className="py-4">
          <p className="text-gray-600 mb-4">Edit the event type name below:</p>
          <Input
            placeholder="Enter event type name..."
            value={editValues.eventType.name}
            onChange={(e) => handleEditInputChange("eventType", e.target.value)}
            onKeyPress={(e) => handleEditKeyPress(e, "eventType")}
            autoFocus
          />
        </div>
      </Modal>

      {/* Edit Modal — Dietary Preferences */}
      <Modal
        title="Edit Dietary Preference"
        open={showEditModal.dietaryPreferences}
        onOk={() =>
          handleUpdateTag(
            "dietaryPreferences",
            editValues.dietaryPreferences.name,
          )
        }
        onCancel={() => {
          setShowEditModal((prev) => ({ ...prev, dietaryPreferences: false }));
          setEditValues((prev) => ({
            ...prev,
            dietaryPreferences: { id: null, name: "", originalName: "" },
          }));
        }}
        okText="Update"
        cancelText="Cancel"
        confirmLoading={isUpdatingDietaryPreference}
      >
        <div className="py-4">
          <p className="text-gray-600 mb-4">
            Edit the dietary preference details below:
          </p>
          <div className="space-y-4">
            <Input
              placeholder="Enter dietary preference name..."
              value={editValues.dietaryPreferences.name}
              onChange={(e) =>
                handleEditInputChange("dietaryPreferences", e.target.value)
              }
              onKeyPress={(e) => handleEditKeyPress(e, "dietaryPreferences")}
              autoFocus
            />
            <Input.TextArea
              placeholder="Enter description (optional)..."
              value={editValues.dietaryPreferences.description}
              onChange={(e) =>
                handleEditDescriptionChange(
                  "dietaryPreferences",
                  e.target.value,
                )
              }
              rows={3}
            />
          </div>
        </div>
      </Modal>

      {/* Edit Modal — Beverage Type */}
      <Modal
        title="Edit Beverage Type"
        open={showEditModal.beverageType}
        onOk={() =>
          handleUpdateTag("beverageType", editValues.beverageType.name)
        }
        onCancel={() => {
          setShowEditModal((prev) => ({ ...prev, beverageType: false }));
          setEditValues((prev) => ({
            ...prev,
            beverageType: {
              id: null,
              name: "",
              originalName: "",
              description: "",
            },
          }));
        }}
        okText="Update"
        cancelText="Cancel"
        confirmLoading={isUpdatingBeverageType}
      >
        <div className="py-4">
          <p className="text-gray-600 mb-4">
            Edit the beverage type details below:
          </p>
          <div className="space-y-4">
            <Input
              placeholder="Enter beverage type name..."
              value={editValues.beverageType.name}
              onChange={(e) =>
                handleEditInputChange("beverageType", e.target.value)
              }
              onKeyPress={(e) => handleEditKeyPress(e, "beverageType")}
              autoFocus
            />
            <Input.TextArea
              placeholder="Enter description (optional)..."
              value={editValues.beverageType.description}
              onChange={(e) =>
                handleEditDescriptionChange("beverageType", e.target.value)
              }
              rows={3}
            />
          </div>
        </div>
      </Modal>

      {/* Edit Modal — Package Type */}
      <Modal
        title="Edit Package Type"
        open={showEditModal.packageType}
        onOk={() => handleUpdateTag("packageType", editValues.packageType.name)}
        onCancel={() => {
          setShowEditModal((prev) => ({ ...prev, packageType: false }));
          setEditValues((prev) => ({
            ...prev,
            packageType: {
              id: null,
              name: "",
              amount: "",
              originalName: "",
              description: "",
            },
          }));
        }}
        okText="Update"
        cancelText="Cancel"
        confirmLoading={isUpdatingPackageType}
      >
        <div className="py-4">
          <p className="text-gray-600 mb-4">
            Edit the package type details below:
          </p>
          <div className="space-y-4">
            <Input
              placeholder="Enter package type name..."
              value={editValues.packageType.name}
              onChange={(e) =>
                handleEditInputChange("packageType", e.target.value)
              }
              onKeyPress={(e) => handleEditKeyPress(e, "packageType")}
              autoFocus
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price
              </label>
              <Input
                placeholder="e.g. 5000"
                prefix="₦"
                value={editValues.packageType.amount}
                onChange={(e) => {
                  const raw = e.target.value.replace(/,/g, "");
                  if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
                    handleEditAmountChange("packageType", raw);
                  }
                }}
              />
            </div>
            <Input.TextArea
              placeholder="Enter description (optional)..."
              value={editValues.packageType.description}
              onChange={(e) =>
                handleEditDescriptionChange("packageType", e.target.value)
              }
              rows={3}
            />
          </div>
        </div>
      </Modal>

      {/* Edit Modal — Service Type */}
      <Modal
        title="Edit Service Type"
        open={showEditModal.serviceType}
        onOk={() => handleUpdateTag("serviceType", editValues.serviceType.name)}
        onCancel={() => {
          setShowEditModal((prev) => ({ ...prev, serviceType: false }));
          setEditValues((prev) => ({
            ...prev,
            serviceType: {
              id: null,
              name: "",
              originalName: "",
              description: "",
            },
          }));
        }}
        okText="Update"
        cancelText="Cancel"
        confirmLoading={isUpdatingServiceType}
      >
        <div className="py-4">
          <p className="text-gray-600 mb-4">
            Edit the service type details below:
          </p>
          <div className="space-y-4">
            <Input
              placeholder="Enter service type name..."
              value={editValues.serviceType.name}
              onChange={(e) =>
                handleEditInputChange("serviceType", e.target.value)
              }
              onKeyPress={(e) => handleEditKeyPress(e, "serviceType")}
              autoFocus
            />
            <Input.TextArea
              placeholder="Enter description (optional)..."
              value={editValues.serviceType.description}
              onChange={(e) =>
                handleEditDescriptionChange("serviceType", e.target.value)
              }
              rows={3}
            />
          </div>
        </div>
      </Modal>

      {/* Edit Modal — Unit of Measurement */}
      <Modal
        title="Edit Unit of Measurement"
        open={showEditModal.unitOfMeasurement}
        onOk={() =>
          handleUpdateTag(
            "unitOfMeasurement",
            editValues.unitOfMeasurement.name,
          )
        }
        onCancel={() => {
          setShowEditModal((prev) => ({ ...prev, unitOfMeasurement: false }));
          setEditValues((prev) => ({
            ...prev,
            unitOfMeasurement: {
              id: null,
              name: "",
              label: "",
              originalName: "",
              description: "",
            },
          }));
        }}
        okText="Update"
        cancelText="Cancel"
        confirmLoading={isUpdatingMeasurement}
      >
        <div className="py-4">
          <p className="text-gray-600 mb-4">
            Edit the unit of measurement details below:
          </p>
          <div className="space-y-4">
            <Input
              placeholder="Enter unit of measurement name..."
              value={editValues.unitOfMeasurement.name}
              onChange={(e) =>
                handleEditInputChange("unitOfMeasurement", e.target.value)
              }
              onKeyPress={(e) => handleEditKeyPress(e, "unitOfMeasurement")}
              autoFocus
            />
            <Input
              placeholder="Enter label (e.g., Kg, L, pcs)..."
              value={editValues.unitOfMeasurement.label}
              onChange={(e) =>
                handleEditLabelChange("unitOfMeasurement", e.target.value)
              }
            />
            <Input.TextArea
              placeholder="Enter description (optional)..."
              value={editValues.unitOfMeasurement.description}
              onChange={(e) =>
                handleEditDescriptionChange("unitOfMeasurement", e.target.value)
              }
              rows={3}
            />
          </div>
        </div>
      </Modal>

      {/* Edit Modal — Delivery Mode */}
      <Modal
        title="Edit Delivery Mode"
        open={showEditModal.deliveryMode}
        onOk={() =>
          handleUpdateTag("deliveryMode", editValues.deliveryMode.name)
        }
        onCancel={() => {
          setShowEditModal((prev) => ({ ...prev, deliveryMode: false }));
          setEditValues((prev) => ({
            ...prev,
            deliveryMode: {
              id: null,
              name: "",
              originalName: "",
              description: "",
            },
          }));
        }}
        okText="Update"
        cancelText="Cancel"
        confirmLoading={isUpdatingDeliveryMode}
      >
        <div className="py-4">
          <p className="text-gray-600 mb-4">
            Edit the delivery mode details below:
          </p>
          <div className="space-y-4">
            <Input
              placeholder="Enter delivery mode name..."
              value={editValues.deliveryMode.name}
              onChange={(e) =>
                handleEditInputChange("deliveryMode", e.target.value)
              }
              onKeyPress={(e) => handleEditKeyPress(e, "deliveryMode")}
              autoFocus
            />
            <Input.TextArea
              placeholder="Enter description (optional)..."
              value={editValues.deliveryMode.description}
              onChange={(e) =>
                handleEditDescriptionChange("deliveryMode", e.target.value)
              }
              rows={3}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GeneralSetup;
